import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { telemetry } from '@/telemetry'
import { getPreviousPath } from './navHistory'
import { usePageTitle } from '@/features/_shared/usePageTitle'

// The SPA catch-all (router.tsx `path: '*'`). Because the server serves index.html
// for every unmatched path (MapFallbackToFile), the backend never sees a 404 for a
// bogus URL — only the client knows the route didn't match. So this is the single
// place a dead/mistyped URL surfaces, and the only place we can record it.
export function NotFound() {
  const { pathname } = useLocation()

  usePageTitle('Page Not Found')

  // Record the miss so dead links become visible in App Insights (customEvents),
  // which is what alerting watches. No-op when telemetry isn't configured;
  // trackEvent can never throw. Fires once per distinct path landed on.
  //
  // `referrerPath` (the prior IN-APP route) is the useful signal for chasing
  // internal broken links — it turns "/foo 404'd" into "someone clicked a bad
  // link from /league/42/standings". `referrer`/`userAgent` help distinguish
  // real inbound dead links and bots/scanners from genuine user mistakes.
  useEffect(() => {
    telemetry.trackEvent('PageNotFound', {
      path: pathname,
      referrerPath: getPreviousPath() ?? '(direct)',
      referrer: document.referrer || '(none)',
      userAgent: navigator.userAgent,
    })
  }, [pathname])

  return (
    <div className="mx-auto max-w-xl py-16 px-4 text-center">
      <h1 className="text-2xl font-heading font-bold text-navy">Page Not Found</h1>
      <p className="mt-4 text-slate-600">
        We couldn&rsquo;t find the page you were looking for. It may have moved, or
        the link may be broken.
      </p>

      <div className="mt-8">
        <a href="/" className="text-blue hover:underline">
          Back to home
        </a>
      </div>
    </div>
  )
}
